import { ApiError } from './apiError';

/**
 * Takes the reader to the field the server rejected.
 *
 * A form that refuses a submission owes three things, and a banner alone gives one of them:
 * it must SAY which field, SHOW it, and — on a stepped form — GO to the step holding it. A
 * reader on step 2 of a wizard, told "one or more validation errors occurred", has to open
 * every step and read every input to find the one at fault.
 *
 * Call it from the catch block after a write:
 *
 * ```ts
 * catch (caught) {
 *   const failure = caught instanceof ApiError ? caught : new ApiError('…', 0);
 *   setError(failure);
 *   focusValidationError(failure, { idPrefix: 'plantilla-position' });
 * }
 * ```
 */
export interface ValidationFocusOptions {
  /**
   * The modal's id prefix. A field resolves to `${idPrefix}-${kebab(field)}` — `sortOrder`
   * becomes `plantilla-position-sort-order` — which is the naming every shared input already
   * follows.
   */
  idPrefix: string;

  /**
   * Field name to element id, for the ones the convention does not reach: a field the form
   * renders under a different name, or one input standing for several server fields.
   */
  fieldIds?: Record<string, string>;

  /**
   * Which step holds a field, on a stepped form. Returning undefined means "wherever it is,
   * it is already on screen".
   */
  stepOf?: (field: string) => number | undefined;

  /** Switches to that step. Required for `stepOf` to do anything. */
  onStep?: (index: number) => void;
}

/** `sortOrder` → `sort-order`. */
function kebab(field: string): string {
  return field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

function elementIdFor(field: string, options: ValidationFocusOptions): string {
  return options.fieldIds?.[field] ?? `${options.idPrefix}-${kebab(field)}`;
}

/**
 * Scrolls the first rejected field into view and focuses it, switching step first when the
 * form has one.
 *
 * Returns the field it went to, or null when the failure named no field — a conflict, a
 * permission refusal, a network error. Those are not a field's fault and there is nothing to
 * point at.
 */
export function focusValidationError(
  error: ApiError,
  options: ValidationFocusOptions,
): string | null {
  if (!error.isValidation) {
    return null;
  }

  /*
    Server order, not DOM order. The server lists the fields in the order its validator
    declared them, which follows the form; re-sorting by position would need every field
    mounted, and on a stepped form most of them are not.
  */
  const field = Object.keys(error.fields)[0];

  if (field === undefined) {
    return null;
  }

  const step = options.stepOf?.(field);

  if (step !== undefined && options.onStep) {
    options.onStep(step);
  }

  /*
    After the step switch, not before: the input is not in the DOM until its step renders, so
    querying now would find nothing and the reader would be moved to the right step and left
    staring at it. Two frames, because the first only commits the state change.
  */
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const elementId = elementIdFor(field, options);
      const element = document.getElementById(elementId);

      if (!element) {
        /*
          Loud, because the failure mode is silence: the server named a field, the reader was
          told which, and nothing moved. That looks like the feature is not wired at all.

          Almost always an id that does not follow `${idPrefix}-${kebab(field)}` — `amount`
          for a field the server calls `monthlyAmount`, `unit` for `orgUnitId`. Either rename
          the input to match the field, or map it in `fieldIds`.
        */
        if (import.meta.env.DEV) {
          console.error(
            `[validationFocus] The server rejected "${field}" but no element has id ` +
              `"${elementId}". Rename the input to match the field, or add it to fieldIds.`,
          );
        }

        return;
      }

      element.scrollIntoView({ block: 'center', behavior: 'smooth' });

      /*
        Focus after the scroll is asked for, and never `preventScroll` — focusing IS what
        tells a screen-reader user where the form went, and the two have to agree about which
        field that is.
      */
      element.focus();
    });
  });

  return field;
}
